import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Building2, Mail, Phone } from "lucide-react";

const EMPTY = { name: "", company: "", email: "", phone: "", address: "", notes: "" };

export default function Clients() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/clients")).data,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) =>
      editing ? (await api.put(`/clients/${editing.id}`, payload)).data : (await api.post("/clients", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(editing ? "Client updated" : "Client added");
      setFormOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/clients/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Client deleted");
      setDeleteId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const openAdd = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setFormOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#0F172A]">Clients</h1>
          <p className="text-slate-500 mt-1">{clients.length} clients on record.</p>
        </div>
        <Button onClick={openAdd} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="add-client-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add Client
        </Button>
      </div>

      <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#0A2540]" /></TableCell></TableRow>
              ) : clients.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">No clients yet. Add your first client.</TableCell></TableRow>
              ) : (
                clients.map((c) => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-[#0F172A]">{c.name}</TableCell>
                    <TableCell className="text-slate-600">{c.company || "—"}</TableCell>
                    <TableCell className="text-slate-600">{c.email || "—"}</TableCell>
                    <TableCell className="text-slate-600 font-mono text-sm">{c.phone || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-xs truncate">{c.address || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setDeleteId(c.id)} data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="client-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={set("name")} placeholder="Contact person" data-testid="client-name" /></div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Company</Label><Input value={form.company} onChange={set("company")} data-testid="client-company" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="For client portal login" data-testid="client-email" /></div>
              <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="client-phone" /></div>
            </div>
            <div className="space-y-1.5"><Label>Address</Label><Textarea value={form.address} onChange={set("address")} data-testid="client-address" /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} data-testid="client-notes" /></div>
            <p className="text-xs text-slate-400">Tip: the email set here is used to let a client log in and view only their own rentals.</p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" data-testid="client-cancel">Cancel</Button></DialogClose>
            <Button onClick={() => { if (!form.name) { toast.error("Name is required"); return; } saveMutation.mutate(form); }} disabled={saveMutation.isPending} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="client-save">
              {saveMutation.isPending ? "Saving…" : editing ? "Update" : "Add Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this client?</AlertDialogTitle>
            <AlertDialogDescription>Clients with active rentals cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate(deleteId)} data-testid="delete-client-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
